/**
 * Targets — design/PROTOTYPE-SPEC.md §13. Premium.
 *
 * Two of the spec's three tabs. **This month** is the progress bar toward
 * break-even then target, with a stepper per treatment. **My target** is the
 * costs editor, the profit goal, and the equation they produce.
 *
 * *Plan ahead* — per-month treatment packages and the AI marketing assistant —
 * is the next slice. `docs/AI-ASSISTANT.md` holds the model choice and the
 * spend cap that has to be settled before the assistant can ship.
 *
 * ## The bar carries two markers, not one
 *
 * Break-even and target are different facts and a clinic needs both: covering
 * costs is survival, hitting the target is the point. One marker would collapse
 * "you are not losing money" into "you have not succeeded", which are very
 * different things to read on the 20th of the month.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  PlanRequiredError,
  getTargets,
  logTreatmentEvent,
  saveTargets,
  undoTreatment,
  type Me,
  type TargetsResponse,
} from '../lib/api.js';
import { PlanLocked } from './PlanLocked.js';
import { PlanAhead } from './PlanAhead.js';
import {
  formatAmount,
  parseAmount,
  planTreatment,
  type Plan,
  type RecipeProduct,
  type Role as CoreRole,
} from '@inventory-tracker/core';
import { c, field, mono, primaryButton, sans, segTab, segTrack } from '../theme.js';
import {
  cardStyle,
  money,
  moneyRounded,
  penceToPounds,
  poundsToPence,
  smallButton,
} from './format.js';

interface Props {
  me: Me;
  onChanged: () => void;
  refreshToken: number;
}

type Tab = 'month' | 'target' | 'plan';

const AVAILABILITY_TONE = {
  blocked: { bg: c.dangerBg, fg: c.danger, label: 'Blocked' },
  low: { bg: c.amberBg, fg: c.amberText, label: 'Low' },
  ok: { bg: 'transparent', fg: c.muted2, label: '' },
} as const;

export function TargetsPage({ me, onChanged, refreshToken }: Props) {
  const [data, setData] = useState<TargetsResponse | null>(null);
  const [tab, setTab] = useState<Tab>('month');
  const [locked, setLocked] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setData(await getTargets());
  }, []);

  useEffect(() => {
    void load().catch((e: unknown) => {
      if (e instanceof PlanRequiredError) {
        setLocked(e.requiredPlan);
        return;
      }
      setError(e instanceof Error ? e.message : 'Could not load your targets');
    });
  }, [load, refreshToken]);

  /** Resolves true when the change saved, so a form can say so. */
  async function mutate(work: () => Promise<unknown>): Promise<boolean> {
    setError(null);
    try {
      await work();
      await load();
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save');
      return false;
    }
  }

  if (locked) {
    return (
      <PlanLocked
        requiredPlan={locked}
        cycle={me.subscription.cycle}
        role={me.user.role as CoreRole}
        title="Know what the month has to bring in"
        blurb="Put your running costs and profit goal in once, and every month tells you what you have to earn, how far through it you are, and how many of each treatment would get you there, with a warning when the stock to deliver one has run out."
      />
    );
  }

  if (error && !data) {
    return (
      <div role="alert" style={alertStyle}>
        {error}
      </div>
    );
  }
  if (!data) return <p style={{ fontSize: 13.5, color: c.muted2 }}>Loading…</p>;

  return (
    <div data-tour="monthbar">
      {error && (
        <div role="alert" style={{ ...alertStyle, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ ...segTrack, marginBottom: 16 }}>
        {(
          [
            ['month', 'This month'],
            ['target', 'My target'],
            ['plan', 'Plan ahead'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={segTab(tab === key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'month' && (
        <ThisMonth
          data={data}
          onLog={(id, substitutions) => mutate(() => logTreatmentEvent(id, substitutions))}
          onUndo={(id) => mutate(() => undoTreatment(id))}
        />
      )}
      {tab === 'target' && (
        <MyTarget data={data} onSave={(input) => mutate(() => saveTargets(input))} />
      )}
      {tab === 'plan' && (
        <PlanAhead
          // The account's month from the server's clock, never the browser's:
          // a clinic in another time zone must not plan into the wrong month.
          currentMonth={data.monthKey}
          plan={me.subscription.plan as Plan}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

const alertStyle: React.CSSProperties = {
  background: c.dangerBg,
  border: `1px solid ${c.dangerBorder}`,
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 13.5,
  color: c.danger,
};

function ThisMonth({
  data,
  onLog,
  onUndo,
}: {
  data: TargetsResponse;
  onLog: (
    treatmentId: string,
    substitutions: { productId: string; withProductId: string }[],
  ) => Promise<boolean>;
  onUndo: (treatmentId: string) => Promise<boolean>;
}) {
  const { progress: p, pace } = data;
  /** The treatment whose missing products are being swapped, if any. */
  const [swapping, setSwapping] = useState<string | null>(null);
  /** For that treatment: the missing product's id → the product chosen instead. */
  const [swaps, setSwaps] = useState<Record<string, string>>({});
  const [working, setWorking] = useState<string | null>(null);

  const productById = new Map(data.products.map((x) => [x.id, x]));
  /** The shelf as core's planner wants it; the same plan the server runs. */
  const shelf = new Map<string, RecipeProduct>(
    data.products.map((x) => [
      x.id,
      {
        productId: x.id,
        name: x.name,
        qtyOnHand: x.qtyOnHand,
        packContentsHundredths: x.packContentsHundredths,
        openHundredths: x.openHundredths,
      },
    ]),
  );

  /** "40 units Azzalure, 1 Lidocaine": what one of these takes off the shelf. */
  function recipeText(recipe: { productId: string; amountHundredths: number }[]): string {
    return recipe
      .map((line) => {
        const product = productById.get(line.productId);
        if (!product) return null;
        const unit = product.contentUnit ? ` ${product.contentUnit}` : '';
        return `${formatAmount(line.amountHundredths)}${unit} ${product.name}`;
      })
      .filter(Boolean)
      .join(', ');
  }

  async function press(treatmentId: string, blocked: boolean) {
    // A blocked treatment opens the substitute picker instead of failing.
    if (blocked) {
      setSwapping(treatmentId);
      setSwaps({});
      return;
    }
    setWorking(treatmentId);
    await onLog(treatmentId, []);
    setWorking(null);
  }

  /**
   * One sentence saying where the month stands. Ordered by what matters: not
   * started, then smashed, then covered, then still climbing — because the
   * first thing a clinic wants to know is whether the costs are paid.
   */
  const status =
    p.earnedPence === 0
      ? 'Log a treatment or a retail sale to start filling the bar.'
      : p.targetReached
        ? `Target passed, ${money(p.earnedPence - p.targetPence)} past your goal.`
        : p.costsCovered
          ? `Costs covered, ${money(p.profitSoFarPence)} profit so far · ${money(p.toTargetPence)} to your goal.`
          : `${money(p.toBreakEvenPence)} more to cover your running costs.`;

  const statusColor = p.earnedPence === 0 ? c.muted2 : p.costsCovered ? c.success : c.green;

  return (
    <>
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 26, margin: 0 }}>
            {money(p.earnedPence)}
            <span style={{ fontSize: 15, color: c.muted, fontFamily: sans, marginLeft: 8 }}>
              of {money(p.targetPence)}
            </span>
          </h2>
          <span style={{ fontSize: 12.5, color: c.muted2 }}>
            Day {pace.dayOfMonth} of {pace.daysInMonth}
          </span>
        </div>

        {/* The track. Two markers because covering costs and hitting the goal
            are different facts — see the note at the top of the file. */}
        <div style={{ position: 'relative', margin: '20px 0 10px' }}>
          <div style={{ height: 14, background: c.well, borderRadius: 980, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${p.earnedPercent}%`,
                background: p.costsCovered ? c.success : c.green,
                borderRadius: 980,
                transition: 'width 450ms cubic-bezier(0.22,1,0.36,1)',
              }}
            />
          </div>
          {p.targetPence > 0 && (
            <>
              <Marker percent={p.breakEvenPercent} label="Break-even" dashed />
              <Marker percent={p.targetPercent} label="Target" />
            </>
          )}
        </div>

        <p style={{ fontSize: 13.5, color: statusColor, margin: '14px 0 0', fontWeight: 500 }}>
          {status}
        </p>

        {/* Where the money came from, and what the stock behind it cost. The
            cost is shown beside the takings, not taken off the bar: the bar is
            money in against the costs the clinic typed in above. */}
        {p.earnedPence > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 10,
              marginTop: 16,
            }}
          >
            <Source
              label="Treatments"
              value={money(p.treatmentsPence)}
              sub={`${p.treatmentsDone} logged`}
            />
            <Source
              label="Retail sales"
              value={money(p.retailSalesPence)}
              sub={p.retailSalesPence === 0 ? 'none sold yet' : 'products sold'}
            />
            <Source
              label="Stock it used"
              value={money(p.stockCostPence)}
              sub="what those products cost you"
            />
            <Source
              label="Left after stock"
              value={money(p.afterStockPence)}
              sub="before your running costs"
            />
          </div>
        )}

        {pace.deltaPence !== null && p.earnedPence > 0 && (
          <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
            <Figure
              label="Pace"
              value={`${pace.onPace ? '+' : '−'}${moneyRounded(Math.abs(pace.deltaPence))}`}
              tone={pace.onPace ? c.success : c.danger}
              sub={
                pace.onPace ? 'ahead of where you need to be' : 'behind for this point in the month'
              }
            />
            {pace.projectedPence !== null && (
              <Figure
                label="Projected month-end"
                value={moneyRounded(pace.projectedPence)}
                tone={pace.projectedHitsTarget ? c.success : c.green}
                sub={
                  pace.projectedHitsTarget
                    ? 'on course to hit target'
                    : 'short of target at this pace'
                }
              />
            )}
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
          What you have done
        </h2>
        <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 14px', lineHeight: 1.7 }}>
          Tap + each time you do one. It counts toward the target and takes what the treatment
          uses off your stock. Tap − to undo the last one.
        </p>

        {data.treatments.length === 0 ? (
          <p style={{ fontSize: 13.5, color: c.muted2, margin: 0 }}>
            No treatments set up yet. Add them on the My target tab.
          </p>
        ) : (
          data.treatments.map((t) => {
            const tone = AVAILABILITY_TONE[t.availability.key];
            return (
              <div
                key={t.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 0',
                  borderTop: `1px solid ${c.border}`,
                }}
              >
                {swapping === t.id && (
                  <SwapPanel
                    treatment={t}
                    data={data}
                    shelf={shelf}
                    swaps={swaps}
                    setSwaps={setSwaps}
                    busy={working === t.id}
                    onCancel={() => setSwapping(null)}
                    onLog={async (substitutions) => {
                      setWorking(t.id);
                      const ok = await onLog(t.id, substitutions);
                      setWorking(null);
                      if (ok) setSwapping(null);
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0, order: -1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: 14 }}>{t.name}</strong>
                    {tone.label && (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 980,
                          fontSize: 10,
                          fontWeight: 400,
                          textTransform: 'lowercase', fontFamily: mono,
                          background: tone.bg,
                          color: tone.fg,
                        }}
                      >
                        {tone.label}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: c.muted2, marginTop: 2 }}>
                    {money(t.pricePence)} each
                    {t.count > 0 && ` · ${money(t.pricePence * t.count)} so far`}
                    {t.availability.key === 'blocked' &&
                      ` · not enough ${t.availability.missing.join(', ')}`}
                    {t.availability.key === 'low' &&
                      ` · running low on ${t.availability.low.join(', ')}`}
                  </div>
                  {t.recipe.length > 0 && (
                    <div style={{ fontSize: 12.5, color: c.faint, marginTop: 1 }}>
                      Uses {recipeText(t.recipe)}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, order: -1 }}>
                  <button
                    type="button"
                    aria-label={`One fewer ${t.name}`}
                    disabled={t.count === 0 || working === t.id}
                    title="Undo the last one logged. Its stock goes back."
                    onClick={() => {
                      setWorking(t.id);
                      void onUndo(t.id).then(() => setWorking(null));
                    }}
                    style={{ ...smallButton, width: 34, opacity: t.count === 0 ? 0.4 : 1 }}
                  >
                    −
                  </button>
                  <span
                    style={{
                      fontFamily: sans, letterSpacing: '-0.03em',
                      fontSize: 20, fontWeight: 300,
                      minWidth: 28,
                      textAlign: 'center',
                      color: t.count === 0 ? c.muted2 : c.ink,
                    }}
                  >
                    {t.count}
                  </span>
                  <button
                    type="button"
                    aria-label={`One more ${t.name}`}
                    disabled={working === t.id}
                    onClick={() => void press(t.id, t.availability.key === 'blocked')}
                    style={{ ...smallButton, width: 34 }}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

/**
 * Shown when a treatment cannot be logged because something it uses has run
 * out. Emmanuel's rule (13 October 2026): it must not be logged as if the
 * product were there, but the clinic can say "I used this instead, this time".
 * The swap is recorded with the treatment; the recipe itself is not changed.
 */
function SwapPanel({
  treatment,
  data,
  shelf,
  swaps,
  setSwaps,
  busy,
  onCancel,
  onLog,
}: {
  treatment: TargetsResponse['treatments'][number];
  data: TargetsResponse;
  shelf: Map<string, RecipeProduct>;
  swaps: Record<string, string>;
  setSwaps: (next: Record<string, string>) => void;
  busy: boolean;
  onCancel: () => void;
  onLog: (substitutions: { productId: string; withProductId: string }[]) => Promise<void>;
}) {
  const short = planTreatment(treatment.recipe, shelf).short;
  const productById = new Map(data.products.map((x) => [x.id, x]));
  const [nudge, setNudge] = useState<string | null>(null);

  return (
    <div
      style={{
        flexBasis: '100%',
        background: c.dangerBg,
        border: `1px solid ${c.dangerLine}`,
        borderRadius: 14,
        padding: '14px 16px',
      }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 500, color: '#5A2511' }}>
        {treatment.name} cannot be logged: there is not enough stock.
      </div>
      {short.map((line) => {
        const product = productById.get(line.productId);
        const unit = product?.contentUnit ? ` ${product.contentUnit}` : '';
        // Anything else with stock, or with an open pack, can stand in.
        const options = data.products.filter(
          (x) => x.id !== line.productId && (x.qtyOnHand > 0 || x.openHundredths > 0),
        );
        return (
          <div key={line.productId} style={{ marginTop: 10 }}>
            <div style={{ fontSize: 13.5, color: '#5A2511' }}>
              <strong style={{ fontWeight: 500 }}>{line.name}</strong>: needs{' '}
              {formatAmount(line.amountHundredths)}
              {unit}, you have {formatAmount(line.availableHundredths)}
              {unit}.
            </div>
            <select
              value={swaps[line.productId] ?? ''}
              onChange={(e) => setSwaps({ ...swaps, [line.productId]: e.target.value })}
              aria-label={`Use instead of ${line.name}`}
              style={{ ...field, height: 42, fontSize: 14.5, marginTop: 6, maxWidth: 420 }}
            >
              <option value="">Used a different product this time…</option>
              {options.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} ({x.qtyOnHand} in stock)
                </option>
              ))}
            </select>
          </div>
        );
      })}
      {nudge && <p style={{ fontSize: 13.5, color: '#5A2511', margin: '10px 0 0' }}>{nudge}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            const missing = short.filter((line) => !swaps[line.productId]);
            if (missing.length > 0) {
              setNudge(
                `Choose what you used instead of ${missing.map((m) => m.name).join(', ')}, or add stock first.`,
              );
              return;
            }
            void onLog(
              short.map((line) => ({
                productId: line.productId,
                withProductId: swaps[line.productId]!,
              })),
            );
          }}
          style={{ ...primaryButton, width: 'auto', height: 40, padding: '0 18px', fontSize: 14.5 }}
        >
          {busy ? 'Logging…' : 'Log with these instead'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{ ...smallButton, height: 40, padding: '0 16px', background: c.white }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Marker({ percent, label, dashed }: { percent: number; label: string; dashed?: boolean }) {
  // Hidden at the extremes: a marker sitting on 0% or 100% is indistinguishable
  // from the edge of the track and its label collides with the numbers above.
  if (percent <= 2 || percent >= 99) return null;
  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: -4,
          bottom: -4,
          left: `${percent}%`,
          borderLeft: dashed ? `2px dashed ${c.ink}` : `2px solid ${c.success}`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: -20,
          left: `${percent}%`,
          transform: 'translateX(-50%)',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.5px',
          color: dashed ? c.ink : c.success,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    </>
  );
}

function Figure({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11.5,
          color: c.muted2,
          textTransform: 'lowercase', fontFamily: mono,
        }}
      >
        {label}
      </div>
      <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 22, fontWeight: 300, marginTop: 2, color: tone ?? c.ink }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: c.muted2, marginTop: 1 }}>{sub}</div>
    </div>
  );
}

function MyTarget({
  data,
  onSave,
}: {
  data: TargetsResponse;
  onSave: (input: {
    profitGoalPence?: number;
    costs?: { label: string; amountPence: number }[];
    treatments?: {
      id?: string;
      name: string;
      pricePence: number;
      productIds: string[];
      recipe?: { productId: string; amountHundredths: number }[];
    }[];
  }) => Promise<boolean>;
}) {
  const [costs, setCosts] = useState(
    data.costs.map((c2) => ({ label: c2.label, amount: penceToPounds(c2.amountPence) })),
  );
  const [profitGoal, setProfitGoal] = useState(penceToPounds(data.profitGoalPence));
  const [treatments, setTreatments] = useState(
    data.treatments.map((t) => ({
      id: t.id as string | undefined,
      name: t.name,
      price: penceToPounds(t.pricePence),
      // Amounts are typed text until saved, so "0." can be typed on the way to "0.5".
      recipe: t.recipe.map((line) => ({
        productId: line.productId,
        amount: formatAmount(line.amountHundredths),
      })),
    })),
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** 'saved' or 'failed' after the last press of Save; null before any. */
  const [outcome, setOutcome] = useState<'saved' | 'failed' | null>(null);

  // Live preview of the equation, so the number moves as costs are typed rather
  // than only after saving.
  const costsTotal = costs.reduce((n, x) => n + (poundsToPence(x.amount) ?? 0), 0);
  const goal = poundsToPence(profitGoal) ?? 0;

  /**
   * Awaited, and it says what happened. It used to fire the save and clear
   * "Saving…" in the same tick, so the button never visibly did anything and
   * there was no way to tell a save from a missed click.
   */
  async function save() {
    setOutcome(null);
    // Check every recipe line before sending anything, and say which is wrong.
    for (const t of treatments) {
      if (t.name.trim() === '') continue;
      for (const line of t.recipe) {
        if (line.productId === '') {
          setProblem(`Choose a product for each line of ${t.name}, or remove the empty line.`);
          return;
        }
        if (parseAmount(line.amount) === null) {
          setProblem(
            `${t.name}: the amount "${line.amount}" is not a number above zero. Use up to two decimal places, like 0.5 or 40.`,
          );
          return;
        }
      }
    }
    setProblem(null);
    setBusy(true);
    const ok = await onSave({
      profitGoalPence: goal,
      costs: costs
        .filter((x) => x.label.trim() !== '')
        .map((x) => ({ label: x.label.trim(), amountPence: poundsToPence(x.amount) ?? 0 })),
      treatments: treatments
        .filter((t) => t.name.trim() !== '')
        .map((t) => ({
          ...(t.id ? { id: t.id } : {}),
          name: t.name.trim(),
          pricePence: poundsToPence(t.price) ?? 0,
          productIds: t.recipe.map((line) => line.productId),
          recipe: t.recipe.map((line) => ({
            productId: line.productId,
            amountHundredths: parseAmount(line.amount)!,
          })),
        })),
    });
    setBusy(false);
    setOutcome(ok ? 'saved' : 'failed');
  }

  return (
    <>
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
          What the month has to cover
        </h2>
        <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
          Your running costs plus what you want to take home. Everything else on this screen is
          worked out from these two numbers.
        </p>

        {costs.map((cost, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <input
              value={cost.label}
              onChange={(e) =>
                setCosts(costs.map((x, j) => (i === j ? { ...x, label: e.target.value } : x)))
              }
              placeholder="Rent & rates"
              aria-label="Cost name"
              style={{ ...field, flex: 1 }}
            />
            <input
              value={cost.amount}
              onChange={(e) =>
                setCosts(costs.map((x, j) => (i === j ? { ...x, amount: e.target.value } : x)))
              }
              inputMode="decimal"
              placeholder="0.00"
              aria-label="Cost amount in pounds"
              style={{ ...field, width: 120 }}
            />
            <button
              type="button"
              onClick={() => setCosts(costs.filter((_, j) => j !== i))}
              aria-label={`Remove ${cost.label || 'this cost'}`}
              style={{ ...smallButton, color: c.danger }}
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setCosts([...costs, { label: '', amount: '' }])}
          style={{ ...smallButton, marginTop: 6 }}
        >
          + Add a cost
        </button>

        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${c.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13.5 }}>Profit goal</span>
            <input
              value={profitGoal}
              onChange={(e) => setProfitGoal(e.target.value)}
              inputMode="decimal"
              aria-label="Profit goal in pounds"
              style={{ ...field, width: 120 }}
            />
          </label>
        </div>

        {/* The equation, spelled out. It is the whole mental model of the
            screen and worth stating rather than leaving to be inferred. */}
        <div
          style={{
            background: c.well,
            borderRadius: 12,
            padding: '12px 16px',
            marginTop: 16,
            fontSize: 14,
          }}
        >
          {money(costsTotal)} costs + {money(goal)} profit ={' '}
          <strong>{money(costsTotal + goal)} a month</strong>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
          What you offer
        </h2>
        <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
          List what each treatment uses and how much. Logging one then takes that off your
          stock, and the app tells you when you cannot deliver it. For a shared vial, set what
          one pack holds on the product (Edit product) and enter the amount here, like 40 units.
        </p>

        {treatments.map((t, i) => {
          const live = data.treatments.find((x) => x.id === t.id);
          return (
            <div
              key={t.id ?? `new-${i}`}
              style={{ padding: '12px 0', borderTop: `1px solid ${c.border}` }}
            >
              <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                <input
                  value={t.name}
                  onChange={(e) =>
                    setTreatments(
                      treatments.map((x, j) => (i === j ? { ...x, name: e.target.value } : x)),
                    )
                  }
                  placeholder="Anti-wrinkle · 1 area"
                  aria-label="Treatment name"
                  style={{ ...field, flex: 1 }}
                />
                <input
                  value={t.price}
                  onChange={(e) =>
                    setTreatments(
                      treatments.map((x, j) => (i === j ? { ...x, price: e.target.value } : x)),
                    )
                  }
                  inputMode="decimal"
                  placeholder="0.00"
                  aria-label="Treatment price in pounds"
                  style={{ ...field, width: 110 }}
                />
                <button
                  type="button"
                  onClick={() => setTreatments(treatments.filter((_, j) => j !== i))}
                  aria-label={`Remove ${t.name || 'this treatment'}`}
                  style={{ ...smallButton, color: c.danger }}
                >
                  Remove
                </button>
              </div>

              {live?.neededForTarget !== null && live?.neededForTarget !== undefined && (
                <div style={{ fontSize: 12.5, color: c.muted2, marginBottom: 8 }}>
                  {live.neededForTarget} of these alone would hit the target · about {live.perWeek}
                  /week
                </div>
              )}

              {/* The recipe: one line per product, with how much of it. */}
              <div style={{ display: 'grid', gap: 6 }}>
                {t.recipe.map((line, k) => {
                  const product = data.products.find((x) => x.id === line.productId);
                  const setLine = (next: { productId: string; amount: string }) =>
                    setTreatments(
                      treatments.map((x, j) =>
                        i === j
                          ? { ...x, recipe: x.recipe.map((r, m) => (m === k ? next : r)) }
                          : x,
                      ),
                    );
                  return (
                    <div
                      key={k}
                      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <select
                        value={line.productId}
                        onChange={(e) => setLine({ ...line, productId: e.target.value })}
                        aria-label="Product used"
                        style={{ ...field, height: 42, fontSize: 14.5, flex: '1 1 220px', minWidth: 0 }}
                      >
                        <option value="">Choose a product…</option>
                        {data.products.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name} ({x.qtyOnHand} in stock)
                          </option>
                        ))}
                      </select>
                      <input
                        value={line.amount}
                        onChange={(e) => setLine({ ...line, amount: e.target.value })}
                        inputMode="decimal"
                        placeholder="1"
                        aria-label="How much one treatment uses"
                        style={{ ...field, height: 42, fontSize: 14.5, width: 86 }}
                      />
                      <span style={{ fontSize: 13.5, color: c.muted, minWidth: 96 }}>
                        {product?.contentUnit
                          ? `${product.contentUnit} (pack holds ${formatAmount(product.packContentsHundredths)})`
                          : 'packs each time'}
                      </span>
                      <button
                        type="button"
                        aria-label="Remove this product"
                        onClick={() =>
                          setTreatments(
                            treatments.map((x, j) =>
                              i === j ? { ...x, recipe: x.recipe.filter((_, m) => m !== k) } : x,
                            ),
                          )
                        }
                        style={{ ...smallButton, color: c.muted }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() =>
                    setTreatments(
                      treatments.map((x, j) =>
                        i === j ? { ...x, recipe: [...x.recipe, { productId: '', amount: '1' }] } : x,
                      ),
                    )
                  }
                  style={{ ...smallButton, justifySelf: 'start' }}
                >
                  + Add a product it uses
                </button>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() =>
            setTreatments([...treatments, { id: undefined, name: '', price: '', recipe: [] }])
          }
          style={{ ...smallButton, marginTop: 12 }}
        >
          + Add a treatment
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy}
          onClick={() => void save()}
          style={{
            ...primaryButton,
            width: 'auto',
            height: 44,
            padding: '0 22px',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Saving…' : 'Save targets'}
        </button>
        <span role="status" style={{ fontSize: 14 }}>
          {outcome === 'saved' && (
            <span
              style={{
                background: c.okBg,
                border: `1px solid ${c.okLine}`,
                color: c.okText,
                borderRadius: 980,
                padding: '7px 14px',
                fontWeight: 500,
              }}
            >
              Saved. Your target and treatments are up to date.
            </span>
          )}
          {problem && <span style={{ color: c.danger }}>{problem}</span>}
          {!problem && outcome === 'failed' && (
            <span style={{ color: c.danger }}>
              That did not save. The reason is at the top of the page.
            </span>
          )}
        </span>
      </div>
    </>
  );
}

function Source({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div style={{ background: c.appBg, borderRadius: 12, padding: '11px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 12.5, color: c.muted }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 300, letterSpacing: '-0.4px', marginTop: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: c.muted2, marginTop: 1 }}>{sub}</div>
    </div>
  );
}
